/* Common Imports */

import { Skeleton, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { ObjectId } from "bson";
import React from "react";

/* Icon Imports */

import { EditOutlined, HelpOutline, MoreVert as MoreVertIcon } from "@mui/icons-material";

/* model imports */
import { BusinessProfileInterface, PropertyInterface, UserInterface } from "../../../../models";

/* redux imports */

import StateInterface from "../../../../redux-magic/state-interface";
import { PropertySnackbarInterface } from "../../../../redux-magic/sub-interfaces/property-snackbar";
import { addSaveEntityBusinessProfileThunk, PublishPropertyThunk } from "../../../../redux-magic/thunks";

import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Beegru support, the number the app's "Need help?" dials (CompanyInfo.SUPPORT_PHONE_E164). */
const BEEGRU_SUPPORT_PHONE: string = "+917829371999";
import { useMediaQuery } from "@mui/system";

import { useRouter } from "next/router";
import { LoginFlowTriggers, createLoginFlow } from "../../../../lib/auth/helpers";
import DeletePropertyModal from "../../../modals/PropertyModal/DeletePropertyModal";
import EditPropertyImagesModalMObile from "../../../modals/PropertyModal/EditPropertyImagesModalMobile";
import PropertyTitleEditModal from "../../../modals/PropertyModal/propertyTitleEditModal";
import UploadImagesMobile from "../../../modals/PropertyModal/UploadPropertyImagesMobile";
import ReportModal from "../../../modals/ReportModal";
import ShareModal from "../../../modals/ShareModal";

/* Interfaces */

interface LocalPropertyInterface extends Omit<PropertyInterface, "created_by"> {
	created_by: {
		is_business_profile: boolean;
		business_profile_id: string | ObjectId | BusinessProfileInterface;
		user_id: string | ObjectId | UserInterface;
	};
}

/* stylings */

const Heading = styled(Typography)(({ theme }) => ({
	width: "100%",
	fontWeight: "400",
	fontSize: "1rem",
	[theme.breakpoints.up("sm")]: {
		fontSize: "1rem",
	},
	[theme.breakpoints.up("md1190")]: {
		fontSize: "1.125rem",
	},
}));

const HeadingContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 0.5rem",
}));

const IconContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "flex-end",
	gap: "0.5rem",
}));

const ContentContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 0.75rem 0.5rem 0.75rem",
	gap: "0.5rem",
}));

const TextButton = styled(Typography)(({ theme }) => ({
	fontWeight: 500,
	fontSize: "0.875rem",
}));

const PropertyTitleSection = ({
	propertyData,
	dispatch,
	session,
	profile_context,
	isEditor,
	loading,
	saved_content,
	property_snackbar,
	login_flow_global_state,
	screenType,
}: {
	propertyData: LocalPropertyInterface;
	dispatch: Function;
	session: any;
	index: number;
	profile_context: StateInterface["profile_context"];
	isEditor: boolean;
	loading: boolean;
	saved_content: LocalPropertyInterface["saved_content"];
	property_snackbar: PropertySnackbarInterface;
	login_flow_global_state: StateInterface["login_flow_global_state"];
	screenType: string;
}) => {
	const theme = useTheme();

	const [propertyTitleEditModal, setPropertyTitleEditModal] = React.useState(false);

	const [openProfileModal, setOpenProfileModal] = React.useState(false);

	const [editProfileModal, setEditProfileModal] = React.useState(false);

	const [shareModal, setShareModal] = React.useState(false);

	const [reportModal, setReportModal] = React.useState(false);

	const [deleteModal, setDeleteModal] = React.useState(false);

	const [slugEdited, setSlugEdited] = React.useState(false);

	const [anchorEl, setAnchorEl] = React.useState<Element | null>(null);

	const popOpen = Boolean(anchorEl);

	const id = popOpen ? "simple-popover" : undefined;

	const smDevice = useMediaQuery(theme.breakpoints.down("sm"));

	/*

      & Let's declare the router. Here we are using the useRouter hook from nextjs.

      & We are using the router to get the query params from the url. Memoize the router object to prevent unnecessary re-renders.

      */

	const router = React.useRef(useRouter()).current;

	React.useEffect(() => {
		if (slugEdited)
			router.push(process.env.PRODUCTION_URL + "properties/" + propertyData.slug, undefined, { shallow: true });
	}, [propertyData.slug, slugEdited, router]);

	const localLang = {
		upload_images:
			router.locale === "kn-IN"
				? "ಚಿತ್ರಗಳನ್ನು ಅಪ್ಲೋಡ್ ಮಾಡಿ"
				: router.locale === "ar-AE"
					? "رفع الصور"
					: "Upload images",
		edit_images:
			router.locale === "kn-IN" ? "ಚಿತ್ರಗಳನ್ನು ಸಂಪಾದಿಸಿ" : router.locale === "ar-AE" ? "تعديل الصور" : "Edit images",
		delete_property:
			router.locale === "kn-IN" ? "ಸ್ವೀಕರಿಸಿ" : router.locale === "ar-AE" ? "حذف العقار" : "Delete property",
		save: router.locale === "kn-IN" ? "ಉಳಿಸಿ" : router.locale === "ar-AE" ? "حفظ" : "Save",
		share: router.locale === "kn-IN" ? "ಹಂಚಿಕೊಳ್ಳಿ" : router.locale === "ar-AE" ? "شارك" : "Share",
		report: router.locale === "kn-IN" ? "ವರದಿ" : router.locale === "ar-AE" ? "أبلغ" : "Report",
		add_property_pictures:
			router.locale === "kn-IN"
				? "ಚಿತ್ರಗಳನ್ನು ಸೇರಿಸಿ"
				: router.locale === "ar-AE"
					? "إضافة صور للعقار"
					: "Add property pictures",
		add_photos: router.locale === "kn-IN" ? "ಫೋಟೋಗಳನ್ನು ಸೇರಿಸಿ" : router.locale === "ar-AE" ? "أضف صور" : "Add photos",
		properties_with_more_than_5_pictues:
			router.locale === "kn-IN"
				? propertyData.is_ofb
					? "ಹೆಚ್ಚು ಚಿತ್ರಗಳನ್ನು ಹೊಂದಿರುವ OFB ಗಳು ಹೆಚ್ಚು ಪ್ರಶ್ನೆಗಳನ್ನು ಪಡೆಯುತ್ತವೆ"
					: "ಹೆಚ್ಚು ಚಿತ್ರಗಳನ್ನು ಹೊಂದಿರುವ ಆಸ್ತಿಗಳು ಹೆಚ್ಚು ಪ್ರಶ್ನೆಗಳನ್ನು ಪಡೆಯುತ್ತವೆ"
				: router.locale === "ar-AE"
					? propertyData.is_ofb
						? "أو إف بي التي تحتوي على أكثر من 5 صور تحصل على استفسارات أكثر"
						: "العقارات التي تحتوي على أكثر من 5 صور تحصل على استفسارات أكثر"
					: propertyData.is_ofb
						? "OFBs with more than 5 pictures gets more enquiries"
						: "Properties with more than 5 pictures gets more enquiries",
	};

	const [bookmark, setBookmark] = React.useState(
		saved_content?.find((item) => {
			return profile_context && profile_context.is_business_profile
				? item.saved_by.business_profile_id === profile_context?.business_profile_id
				: item.saved_by.user_id === profile_context?.user_id;
		}) !== undefined,
	);

	const logged_user: ObjectId | string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser._id
					? session.user.dbUser._id
					: ""
				: ""
			: ""
		: "";

	const logged_user_access: string = session
		? session.user
			? session.user.dbUser
				? session.user.dbUser
					? session.user.dbUser.access_level
						? session.user.dbUser.access_level
						: ""
					: ""
				: ""
			: ""
		: "";

	const creator_user_id: string | ObjectId | UserInterface = propertyData.created_by
		? propertyData.created_by.is_business_profile
			? propertyData.created_by.user_id
				? (propertyData.created_by.user_id as UserInterface)._id
				: ""
			: (propertyData.created_by.user_id as UserInterface)._id != ""
				? (propertyData.created_by.user_id as UserInterface)._id
				: ""
		: "";

	const creator_business_profile_id: string | ObjectId | BusinessProfileInterface = propertyData.created_by
		? propertyData.created_by.is_business_profile
			? propertyData.created_by.business_profile_id != ""
				? (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id
				: ""
			: ""
		: "";

	const profile_context_user = profile_context
		? profile_context.is_business_profile
			? profile_context.business_profile_id
			: profile_context.user_id
		: null;

	const creator: string | ObjectId | UserInterface | BusinessProfileInterface = propertyData.created_by
		? propertyData.created_by.is_business_profile
			? propertyData.created_by.business_profile_id != ""
				? (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id
				: (propertyData.created_by.business_profile_id as string)
			: propertyData.created_by.user_id != ""
				? (propertyData.created_by.user_id as UserInterface)._id
				: (propertyData.created_by.user_id as string)
		: "";

	const is_user_creator: boolean =
		logged_user !== "" ? (creator !== "" ? (profile_context_user === creator ? true : false) : false) : false;

	if (loading) {
		return (
			<React.Fragment>
				{is_user_creator ||
				session?.user?.dbUser?.access_level.includes("super_admin") ||
				(session?.user?.dbUser?.access_level.includes("admin") &&
					session?.user?.dbUser?.access_level.includes("property")) ||
				propertyData.transaction_type === "ofb" ? (
					<HeadingContainer
						sx={{
							margin: "0.75rem 0rem 0.5rem 0rem",
							justifyContent:
								is_user_creator ||
								session?.user?.dbUser?.access_level.includes("super_admin") ||
								(session?.user?.dbUser?.access_level.includes("admin") &&
									session?.user?.dbUser?.access_level.includes("property")) ||
								propertyData.transaction_type === "ofb"
									? "space-between"
									: "flex-end",
						}}
					>
						<Skeleton
							animation="wave"
							variant="rectangular"
							width="15rem"
							height="2rem"
							sx={{
								borderRadius: "12px",
							}}
						/>

						<IconContainer
							sx={{
								gap: "1rem",
								[theme.breakpoints.down("sm")]: {
									display: "none",
								},
							}}
						>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="2rem"
								height="2rem"
								sx={{ borderRadius: "50%" }}
							/>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="2rem"
								height="2rem"
								sx={{ borderRadius: "50%" }}
							/>
							<Skeleton
								animation="wave"
								variant="rectangular"
								width="2rem"
								height="2rem"
								sx={{ borderRadius: "50%" }}
							/>
						</IconContainer>
					</HeadingContainer>
				) : null}
			</React.Fragment>
		);
	}

	const handleClick = (event: React.MouseEvent<Element>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	/* Upload Picture modal */

	const handleUploadModalOpen = () => {
		setOpenProfileModal(true);
		handleClose();
	};

	const handleUploadModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setOpenProfileModal(false);
		}
	};

	/* Edit  Picture modal */

	const handleEditProfileModalOpen = () => {
		setEditProfileModal(true);
		handleClose();
	};

	const handleEditProfileModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setEditProfileModal(false);
		}
	};

	/* Share Modal */

	const handleShareModalOpen = () => {
		setShareModal(true);
	};

	const handleShareModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setShareModal(false);
		}
	};

	/* Report Modal */

	const handleReportModalOpen = () => {
		setReportModal(true);
	};

	const handleReportModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setReportModal(false);
		}
	};

	/* Delete modal */

	/* Mark as sold / rented (3 Nov 2026): the dashboard card's call (PublishPropertyThunk, status "sold_out"), then the
	   page reads the listing again. */
	const isSaleType: boolean = ["buy", "resale"].includes(String(propertyData?.transaction_type || ""));
	const [soldConfirmOpen, setSoldConfirmOpen] = React.useState<boolean>(false);
	const [markingSold, setMarkingSold] = React.useState<boolean>(false);
	const markLang = {
		mark_sold: router.locale === "ar-AE" ? "تحديد كمُباع" : router.locale === "kn-IN" ? "ಮಾರಾಟವಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ" : "Mark as sold",
		mark_rented: router.locale === "ar-AE" ? "تحديد كمؤجَّر" : router.locale === "kn-IN" ? "ಬಾಡಿಗೆಗೆ ನೀಡಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಿ" : "Mark as rented",
		confirm_sold:
			router.locale === "ar-AE"
				? "هل تريد تحديد هذا العقار كمُباع؟ لن يظهر للآخرين على الإنترنت بعد الآن."
				: router.locale === "kn-IN"
					? "ಈ ಪಟ್ಟಿಯನ್ನು ಮಾರಾಟವಾಗಿದೆ ಎಂದು ಗುರುತಿಸಬೇಕೇ? ಇದು ಇನ್ನು ಮುಂದೆ ಇತರರಿಗೆ ಆನ್‌ಲೈನ್‌ನಲ್ಲಿ ಕಾಣುವುದಿಲ್ಲ."
					: "Mark this listing as sold? It will no longer be visible to others online.",
		confirm_rented:
			router.locale === "ar-AE"
				? "هل تريد تحديد هذا العقار كمؤجَّر؟ لن يظهر للآخرين على الإنترنت بعد الآن."
				: router.locale === "kn-IN"
					? "ಈ ಪಟ್ಟಿಯನ್ನು ಬಾಡಿಗೆಗೆ ನೀಡಲಾಗಿದೆ ಎಂದು ಗುರುತಿಸಬೇಕೇ? ಇದು ಇನ್ನು ಮುಂದೆ ಇತರರಿಗೆ ಆನ್‌ಲೈನ್‌ನಲ್ಲಿ ಕಾಣುವುದಿಲ್ಲ."
					: "Mark this listing as rented? It will no longer be visible to others online.",
		need_help: router.locale === "ar-AE" ? "تحتاج مساعدة؟" : router.locale === "kn-IN" ? "ಸಹಾಯ ಬೇಕೇ?" : "Need help?",
		cancel: router.locale === "ar-AE" ? "إلغاء" : router.locale === "kn-IN" ? "ರದ್ದುಮಾಡಿ" : "Cancel",
		confirm: router.locale === "ar-AE" ? "تأكيد" : router.locale === "kn-IN" ? "ದೃಢೀಕರಿಸಿ" : "Confirm",
	};
	const markSold = async () => {
		setMarkingSold(true);
		await dispatch(
			PublishPropertyThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				phoneNumber: session ? session.user.dbUser.phone : "",
				countryCode: session ? session.user.dbUser.country_code : "",
				property_id: propertyData ? propertyData._id : "",
				status: "sold_out",
				creator_is_business_profile: isEditor ? profile_context.is_business_profile : propertyData.created_by.is_business_profile,
				creator_user_id: isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id,
				creator_business_profile_id: isEditor
					? profile_context.business_profile_id
					: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id,
				profile_context: profile_context,
			}),
		);
		setMarkingSold(false);
		setSoldConfirmOpen(false);
		router.replace(router.asPath);
	};

	const handleDeleteModalOpen = () => {
		setDeleteModal(true);
		// handleClose();
	};

	const handleDeleteModalClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason !== "backdropClick") {
			setDeleteModal(false);
		}
	};

	const signIn = () => {
		if ((login_flow_global_state?.ui.type || "redirect") === "modal") {
			if (session && session.authorized) {
				new Function(...(login_flow_global_state?.action.sign_in || ["session", "return"])).call(session);
			} else {
				const { startFlow, resetFlow }: LoginFlowTriggers = createLoginFlow({
					dispatch,
					trigger_source: "navbar-dropdown",
					flow_type: "modal",
					autofill: false,
					action: {
						callback_url:
							process.env.PRODUCTION_API_URL +
							"auth/end-session?sessionId=" +
							encodeURIComponent(session?.session_id || "") +
							"&callbackUrl=" +
							encodeURIComponent(router.asPath),
						sign_in: (session: any) => {},
						sign_up: (session: any) => {},
						sign_out: () => {},
					},
				});
				startFlow();
			}
		} else {
		}
	};

	const submit = async () => {
		if (session === null) {
			signIn();
		} else {
			dispatch(
				addSaveEntityBusinessProfileThunk({
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id,
					business_profile_id: profile_context.business_profile_id,
					entity_id: propertyData._id,
					entity_type: "property",
					add_remove: !bookmark ? "add" : "remove",
					url: router.asPath ? router.asPath : "",
					sessionId: session && session.session_id,
				}),
			);
			setBookmark(!bookmark);
		}
	};

	return (
		<React.Fragment>
			<ShareModal
				open={shareModal}
				handleClose={handleShareModalClose}
				url={process.env.PRODUCTION_URL + "properties/" + propertyData.slug}
				session={session}
				dispatch={dispatch}
				entity="property"
				creator_user_id={creator_user_id}
				entity_created_by={propertyData.created_by}
				entity_id={propertyData._id}
				profile_context={profile_context}
			/>

			<ReportModal
				open={reportModal}
				handleClose={handleReportModalClose}
				session={session}
				dispatch={dispatch}
				title="property"
			/>

			<DeletePropertyModal
				creator_user_id={creator_user_id}
				creator_business_profile_id={creator_business_profile_id}
				propertyData={propertyData}
				open={deleteModal}
				handleClose={handleDeleteModalClose}
				dispatch={dispatch}
				isEditor={isEditor}
				profile_context={profile_context}
				session={session}
			/>
			<PropertyTitleEditModal
				session={session}
				open={propertyTitleEditModal}
				handleClose={() => setPropertyTitleEditModal(false)}
				dispatch={dispatch}
				phoneNumber={session ? session.user.dbUser.phone : ""}
				countryCode={session ? session.user.dbUser.country_code : ""}
				property_id={propertyData._id}
				editor_is_business_profile={
					isEditor ? profile_context.is_business_profile : propertyData.created_by.is_business_profile
				}
				editor_user_id={isEditor ? profile_context.user_id : (propertyData.created_by.user_id as UserInterface)._id}
				editor_business_profile_id={
					isEditor
						? profile_context.business_profile_id
						: (propertyData.created_by.business_profile_id as BusinessProfileInterface)._id
				}
				property_title={propertyData.title}
				slugEdited={slugEdited}
				setSlugEdited={setSlugEdited}
				profile_context={profile_context}
			/>
			{is_user_creator ||
			session?.user?.dbUser?.access_level.includes("super_admin") ||
			(session?.user?.dbUser?.access_level.includes("admin") &&
				session?.user?.dbUser?.access_level.includes("property")) ||
			propertyData.transaction_type === "ofb" ? (
				<HeadingContainer>
					<>
						<Heading>
							{propertyData.title}
							<IconButton
								id={screenType + "_spp_property_title_edit_button"}
								color="primary"
								onClick={() => setPropertyTitleEditModal(true)}
								sx={{ margin: "0rem 0rem 0rem 0.5rem" }}
							>
								<EditOutlined
									sx={{
										fontSize: "1.25rem",
										[theme.breakpoints.up("md1190")]: {
											fontSize: "1.5rem",
										},
									}}
								/>
							</IconButton>
						</Heading>
					</>

					{/* <IconContainer> */}
					{/* Save */}

					{/* <IconButton
							disableFocusRipple
							disableRipple
							disableTouchRipple
							onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
								submit();
							}}
							sx={{
								padding: "0rem",
								"&:hover": {
									background: "none",
								},
							}}
						>
							{!bookmark ? (
								<BookmarkBorderOutlined
									sx={{
										color: theme.palette.primary.main,
										fontSize: smDevice ? "1.75rem" : "1.75rem",
									}}
								/>
							) : (
								<Bookmark
									sx={{
										color: theme.palette.primary.main,
										fontSize: smDevice ? "1.75rem" : "1.75rem",
									}}
								/>
							)}
						</IconButton> */}

					{/* Share */}

					{/* {propertyData && propertyData.published ? (
							<IconButton
								sx={{
									[theme.breakpoints.down("sm")]: {
										display: "none",
									},
								}}
								onClick={handleShareModalOpen}
							>
								<Tooltip title={localLang.share}>
									<Share
										sx={{
											width: "1.5rem",
											height: "1.5rem",
											// [theme.breakpoints.down("sm")]: {
											// 	display: "none",
											// },
										}}
										fontSize="large"
									/>
								</Tooltip>
							</IconButton>
						) : null} */}

					{/* More Options */}

					{is_user_creator ||
					(logged_user_access.includes("admin") &&
						logged_user_access.includes("property") &&
						propertyData.assigned_to.includes(logged_user)) ||
					logged_user_access.includes("super_admin") ||
					isEditor ? (
						<IconButton
							id={screenType + "_spp_title_more_options_button"}
							onClick={handleClick}
						>
							{/* <img
									src={
										theme.palette.mode == "dark"
											? "/images/icons/singleproperty/report.svg"
											: "/images/icons/singleproperty/morevert-white.svg"
									}
									alt="Report"
									loading="lazy"
									referrerPolicy="no-referrer"
									style={{ width: "1.5rem", height: "1.5rem" }}
								/> */}
							<MoreVertIcon
								sx={{
									width: "1.5rem",
									height: "1.5rem",
									// [theme.breakpoints.down("sm")]: {
									// 	display: "none",
									// },
								}}
								fontSize="large"
							/>
						</IconButton>
					) : (
						/* "Need help?" for every other viewer (3 Nov 2026, as the app's listing menu shows it to all): it
						   dials Beegru support. The owner has it in the ⋮ menu. */
						<Tooltip title={markLang.need_help}>
							<IconButton
								href={"tel:" + BEEGRU_SUPPORT_PHONE}
								aria-label={markLang.need_help}
							>
								<HelpOutline sx={{ width: "1.5rem", height: "1.5rem" }} />
							</IconButton>
						</Tooltip>
					)}

					{/* Report */}

					{/* {is_user_creator ||
						(logged_user_access.includes("admin") &&
							logged_user_access.includes("property") &&
							propertyData.assigned_to.includes(logged_user)) ||
						logged_user_access.includes("super_admin") ||
						isEditor ? null : (
							<IconButton onClick={handleReportModalOpen}>
								<Tooltip title={localLang.report}>
									<ReportOutlined
										sx={{
											width: "1.5rem",
											height: "1.5rem",
										}}
										fontSize="large"
									/>
								</Tooltip>
							</IconButton>
						)} */}
					{/* </IconContainer> */}
				</HeadingContainer>
			) : null}

			<Popover
				id={id}
				open={popOpen}
				anchorEl={anchorEl}
				disableScrollLock={true}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				slotProps={{
					paper: {
						sx: {
							height: "auto",
							borderRadius: "16px",
							background: theme.palette.background.paper,
							direction: router.locale === "ar-AE" ? "rtl" : "ltr",
							[theme.breakpoints.down("xs")]: {
								width: 300,
								height: 440,
							},
						},
					},
				}}
			>
				<ContentContainer
					sx={{
						cursor: "pointer",
					}}
					onClick={handleUploadModalOpen}
				>
					<TextButton>{localLang.upload_images}</TextButton>
				</ContentContainer>

				{propertyData.images.length > 0 ? (
					<ContentContainer
						sx={{
							cursor: "pointer",
						}}
						onClick={handleEditProfileModalOpen}
					>
						<TextButton>{localLang.edit_images}</TextButton>
					</ContentContainer>
				) : null}

				<ContentContainer
					sx={{
						cursor: "pointer",
					}}
					onClick={handleDeleteModalOpen}
				>
					<TextButton id={screenType + "_spp_delete_property_button"}>{localLang.delete_property}</TextButton>
				</ContentContainer>

				{/* Preview as buyer (3 Nov 2026, the app's listing menu; the page's own ?preview=buyer). */}
				<ContentContainer
					sx={{ cursor: "pointer" }}
					onClick={() => {
						handleClose();
						router.push({ pathname: router.pathname, query: { ...router.query, preview: "buyer" } });
					}}
				>
					<TextButton>
						{router.locale === "ar-AE" ? "معاينة كمشترٍ" : router.locale === "kn-IN" ? "ಖರೀದಿದಾರರಾಗಿ ಪೂರ್ವವೀಕ್ಷಿಸಿ" : "Preview as buyer"}
					</TextButton>
				</ContentContainer>

				{/* Mark as sold / rented (3 Nov 2026, the app's listing menu), after a confirm; not on a draft or one already sold. */}
				{propertyData.status !== "sold_out" && propertyData.status !== "draft" ? (
					<ContentContainer
						sx={{ cursor: "pointer" }}
						onClick={() => {
							handleClose();
							setSoldConfirmOpen(true);
						}}
					>
						<TextButton>{isSaleType ? markLang.mark_sold : markLang.mark_rented}</TextButton>
					</ContentContainer>
				) : null}

				{/* "Need help?" calls Beegru support (the app's listing menu dials the same number). */}
				<ContentContainer
					sx={{ cursor: "pointer" }}
					onClick={() => {
						handleClose();
						window.location.href = "tel:" + BEEGRU_SUPPORT_PHONE;
					}}
				>
					<TextButton>{markLang.need_help}</TextButton>
				</ContentContainer>
			</Popover>

			<Dialog
				open={soldConfirmOpen}
				onClose={() => !markingSold && setSoldConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{isSaleType ? markLang.mark_sold : markLang.mark_rented}</DialogTitle>
				<DialogContent>
					<DialogContentText>{isSaleType ? markLang.confirm_sold : markLang.confirm_rented}</DialogContentText>
				</DialogContent>
				<DialogActions>
					<BeegruButton
						variant="outlined"
						flavor="primary"
						size="small"
						disabled={markingSold}
						onClick={() => setSoldConfirmOpen(false)}
					>
						{markLang.cancel}
					</BeegruButton>
					<BeegruButton
						variant="contained"
						flavor="primary"
						size="small"
						loading={markingSold}
						onClick={markSold}
					>
						{markLang.confirm}
					</BeegruButton>
				</DialogActions>
			</Dialog>

			{/* Upload images modal */}

			{/* {smDevice ? ( */}
			<UploadImagesMobile
				open={openProfileModal}
				handleClose={handleUploadModalClose}
				propertyData={propertyData}
				session={session}
				index={0}
				dispatch={dispatch}
				isEditor={isEditor}
				profile_context={profile_context}
				property_snackbar={property_snackbar as PropertySnackbarInterface}
			/>
			{/* ) : (
				<UploadImageModal
					open={openProfileModal}
					handleClose={handleUploadModalClose}
					propertyData={propertyData}
					session={session}
					index={0}
					dispatch={dispatch}
					isEditor={isEditor}
					profile_context={profile_context}
					property_snackbar={property_snackbar as PropertySnackbarInterface}
				/>
			)} */}

			{/* Edit images modal */}

			{/* {smDevice ? ( */}
			<EditPropertyImagesModalMObile
				openModal={editProfileModal}
				handleClose={handleEditProfileModalClose}
				propertyData={propertyData}
				session={session}
				index={0}
				dispatch={dispatch}
				isEditor={isEditor}
				profile_context={profile_context}
				property_snackbar={property_snackbar as PropertySnackbarInterface}
			/>
			{/* ) : (
				<EditImageModal
					openModal={editProfileModal}
					handleClose={handleEditProfileModalClose}
					propertyData={propertyData}
					session={session}
					index={0}
					dispatch={dispatch}
					isEditor={isEditor}
					profile_context={profile_context}
					property_snackbar={property_snackbar as PropertySnackbarInterface}
				/>
			)} */}
		</React.Fragment>
	);
};

export default PropertyTitleSection;
